import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, fnViaAlias } from '../lib/apiClient';
import { ModalShell } from './PresetEditorModal';
import type { Device, DeviceSchedule, SpeedSetting } from '../lib/types';

/**
 * Edit-schedules modal. Phase 7.5 (#213 deferred follow-up).
 *
 * Backed by `deviceSchedule` handler — accepts the FULL schedule array
 * in one call (per-item MERGE on the server). We send all 3 slots
 * always; that's what iOS does too and what shim parity expects.
 *
 * Pre-condition surfaced by `deviceSchedule.js`: pump must be stopped
 * for the firmware to honor schedule writes (it silently ignores them
 * while running). The handler doesn't block — the SPA shows a
 * one-line warning when the pump is currently ON.
 *
 * Time is HH:MM (24-hour). We ship a single `<input type="time">` per
 * row — browser-native picker, accessible, mobile-friendly. The
 * handler accepts hours/minutes as strings; we parse the value
 * client-side and split on save.
 */

const SLOT_OPTIONS: Array<keyof SpeedSetting> = ['S1', 'S2', 'S3', 'S4'];

interface DraftRow {
  scheduleId: string;
  scheduleName: string;
  isEnabled: boolean;
  speed: keyof SpeedSetting | string;
  startTime: string; // "HH:MM"
  stopTime: string; // "HH:MM"
}

function pad(n: string | number, w = 2): string {
  return String(n).padStart(w, '0');
}

function timeFromSchedule(t: { hours?: string; minutes?: string } | undefined): string {
  return `${pad(t?.hours ?? '0')}:${pad(t?.minutes ?? '0')}`;
}

function splitTime(s: string): { hours: string; minutes: string } {
  // type=time always returns "HH:MM" or "" when empty.
  const [h, m] = s.split(':');
  return { hours: String(Number(h ?? 0)), minutes: String(Number(m ?? 0)) };
}

function rowFromSchedule(s: DeviceSchedule, idx: number): DraftRow {
  return {
    scheduleId: s.scheduleId || String(idx + 1),
    scheduleName: s.scheduleName || `Schedule ${idx + 1}`,
    isEnabled: s.isEnabled === 1,
    speed: s.SchSpeedSelect || 'S1',
    startTime: timeFromSchedule(s.scheduleTime?.startTime),
    stopTime: timeFromSchedule(s.scheduleTime?.stopTime),
  };
}

export function ScheduleEditorModal({
  device,
  onClose,
  onSaved,
}: {
  device: Device;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Always show 3 slots. Pad with empties if the device returned fewer.
  const initialRows = useRef<DraftRow[]>(
    Array.from({ length: 3 }, (_, i) => {
      const existing = device.deviceSchedules?.[i];
      return existing
        ? rowFromSchedule(existing, i)
        : {
            scheduleId: String(i + 1),
            scheduleName: `Schedule ${i + 1}`,
            isEnabled: false,
            speed: 'S1',
            startTime: '00:00',
            stopTime: '00:00',
          };
    }),
  );
  const [rows, setRows] = useState<DraftRow[]>(initialRows.current);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  function patchRow(i: number, patch: Partial<DraftRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const scheduleDataArray = rows.map((r) => {
        const start = splitTime(r.startTime);
        const stop = splitTime(r.stopTime);
        return {
          scheduleId: r.scheduleId,
          scheduleName: r.scheduleName,
          isEnabled: r.isEnabled ? 1 : 0,
          // Handler/iOS use PascalCase `SchSpeedSelect` — DO NOT rename.
          SchSpeedSelect: r.speed,
          scheduleTime: {
            startTime: { hours: start.hours, minutes: start.minutes },
            stopTime: { hours: stop.hours, minutes: stop.minutes },
          },
        };
      });
      await fnViaAlias<null>('PUT', '/devices/device-schedule', {
        deviceId: device.deviceId,
        scheduleDataArray,
      });
      onSaved();
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not save schedule.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Edit schedule" onClose={submitting ? () => {} : onClose}>
      <form onSubmit={onSubmit} noValidate>
        <p className="small text-muted mb-2">
          The pump runs the enabled schedule slots automatically each day.
        </p>

        {device.deviceStatus === 'ON' ? (
          <div
            className="alert alert-warning small mb-3"
            role="status"
          >
            <strong>Tip:</strong> the pump must be off for the firmware to
            apply schedule changes. Turn it off first, then save.
          </div>
        ) : null}

        {rows.map((row, i) => (
          <div
            key={row.scheduleId}
            className="mb-3 p-3"
            style={{
              border: '1px solid var(--aqua-border)',
              borderRadius: 10,
              background: 'var(--aqua-surface-muted, transparent)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong style={{ color: 'var(--aqua-primary)', fontSize: 11, letterSpacing: 1 }}>
                SCHEDULE {i + 1}
              </strong>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`enabled-${i}`}
                  checked={row.isEnabled}
                  onChange={(e) => patchRow(i, { isEnabled: e.target.checked })}
                  disabled={submitting}
                />
                <label
                  className="form-check-label small"
                  htmlFor={`enabled-${i}`}
                >
                  {row.isEnabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>

            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small text-muted">Start</label>
                <input
                  type="time"
                  className="form-control"
                  value={row.startTime}
                  onChange={(e) => patchRow(i, { startTime: e.target.value })}
                  disabled={submitting || !row.isEnabled}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                />
              </div>
              <div className="col-6">
                <label className="form-label small text-muted">Stop</label>
                <input
                  type="time"
                  className="form-control"
                  value={row.stopTime}
                  onChange={(e) => patchRow(i, { stopTime: e.target.value })}
                  disabled={submitting || !row.isEnabled}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                />
              </div>
            </div>

            <div className="mt-2">
              <label className="form-label small text-muted">Preset</label>
              <select
                className="form-select"
                value={row.speed}
                onChange={(e) =>
                  patchRow(i, { speed: e.target.value as keyof SpeedSetting })
                }
                disabled={submitting || !row.isEnabled}
              >
                {SLOT_OPTIONS.map((slot) => {
                  const rpm = device.speedSetting?.[slot] ?? 0;
                  return (
                    <option key={slot} value={slot}>
                      {slot} — {rpm.toLocaleString()} RPM
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        ))}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex gap-2 justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Save schedule'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
